import React, { ChangeEvent, KeyboardEvent, useState } from 'react'
import validEmail from '@hugsmidjan/qj/validEmail'
import { useRouter } from 'next/router'
import { innskraningUrlEnum } from 'stores/onboarding/data'
import { Addresses, User } from 'stores/onboarding/types'

import { ArrowButton } from 'components/button/ButtonWithIcon'
import { Check } from 'components/form-elements/Check'
import { Form } from 'components/form-elements/Form'
import { TextInput } from 'components/form-elements/TextInput'

import { useInnskraningSentryLogging } from 'hooks/onboarding/useInnskraningExceptionCapture'
import { useInnskraningOnMount } from 'hooks/onboarding/useInnskraningOnMount'
import { useOnMount } from 'hooks/useOnMount'
import { Properties } from 'types/onboarding'
import {
  InnskraningAnalyticsStepEnum,
  trackInnskraningEvent,
} from 'utils/onboarding/analytics'

import { PropertyList } from '../property-list/PropertyList'
import { PropertyListItem } from '../property-list/PropertyListItem'
import { StepHeader } from '../step-header/StepHeader'

type Props = {
  user: User
  setUser: (user: User) => void
  onSubmit: () => void
  properties: Properties
  addresses: Addresses
  mailinglist: boolean
  setMailinglist: (mailinglist: boolean) => void
  isKassar: boolean
}

const StandardFlow = ({ properties }: { properties: Properties }) => {
  return (
    <PropertyList
      title={properties.length === 1 ? 'Heimilisfang' : 'Heimilisföng'}
    >
      {properties.map((item, i) => (
        <PropertyListItem
          key={i}
          address={item.address}
          postalCode={item.postalCode}
          city={item.city}
          product={item.product}
        />
      ))}
    </PropertyList>
  )
}

const BoxesFlow = ({ addresses }: { addresses: Addresses }) => {
  return (
    <>
      <PropertyList title="Nýja heimilisfangið:">
        <PropertyListItem
          address={addresses.address}
          postalCode={addresses.postalCode}
          product={addresses.product}
        />
      </PropertyList>
      <PropertyList title="Þú færð flutningskassana senda hingað:">
        <PropertyListItem
          address={addresses.oldAddress ?? ''}
          postalCode={addresses.oldPostalCode ?? ''}
        />
      </PropertyList>
    </>
  )
}

export const Summary = (props: Props) => {
  const {
    user,
    setUser,
    properties,
    addresses,
    onSubmit,
    mailinglist,
    setMailinglist,
    isKassar,
  } = props
  const [errorMessage, setErrorMessage] = useState('')

  const { captureMessage } = useInnskraningSentryLogging()

  const router = useRouter()

  const handleSubmit = () => {
    if (user.email === '') {
      setErrorMessage('Vinsamlegast fyllið út netfang.')
    } else if (validEmail(user.email)) {
      trackInnskraningEvent(InnskraningAnalyticsStepEnum.SUMMARY_PAGELOAD, {
        subscribe: mailinglist,
        postnumer: properties.map((p) => p.postalCode),
      })

      onSubmit()
    } else {
      captureMessage('Invalid email address entered in summary page', () => ({
        tags: {
          component: Summary.name,
        },
        contexts: {
          error: {
            message: 'Invalid email address entered in summary page',
          },
        },
      }))
      setErrorMessage('Vinsamlegast sláðu inn gilt netfang.')
    }
  }

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, email: e.target.value })
    setErrorMessage('')
  }

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    // If enter, trigger forward
    if (e.keyCode === 13) {
      handleSubmit()
    }
  }

  const onReferenceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUser({ ...user, reference: e.target.value })
  }

  useInnskraningOnMount(() => {
    if (properties.length === 0) {
      router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.ADDRESS)
    }
  })

  useOnMount(() => {
    trackInnskraningEvent(InnskraningAnalyticsStepEnum.SUMMARY_PAGELOAD)
  })

  const singlePluralText =
    properties.length === 1
      ? 'Hér fyrir neðan er nýja heimilisfangið sem mun fá rafmagn frá Orkusölunni. Breytingarnar fara fram án þess að valda nokkurri röskun á tengingunni.'
      : 'Hér fyrir neðan eru nýju heimilisföngin sem munu fá rafmagn frá Orkusölunni. Breytingarnar fara fram án þess að valda nokkurri röskun á tengingunum.'

  return (
    <div className="summary">
      <StepHeader title="Samantekt" />
      <StepHeader text={singlePluralText} />
      {isKassar ? (
        <BoxesFlow addresses={addresses} />
      ) : (
        <StandardFlow properties={properties} />
      )}

      <Form>
        <TextInput
          type="email"
          icon="mail"
          className="fi_txt fi_email req boldLabel"
          label="Netfang fyrir samskipti"
          name="email"
          value={user.email}
          onChange={onEmailChange}
          onKeyUp={handleKeyUp}
          placeholder="Sláðu inn netfang"
          boldLabel
          message={errorMessage}
        />

        <Check
          name="mailinglist"
          id="mailinglist"
          value="Ég vil vera með á póstlista til þess að fá fréttir, tilboð og góð ráð. Í hverjum mánuði er dreginn út póstlistavinur sem fær frítt rafmagn fyrir heimilið í einn mánuð!"
          label={
            <>
              Ég vil vera með á póstlista til þess að fá fréttir, tilboð og góð
              ráð.
            </>
          }
          checked={mailinglist}
          onChange={(e) => {
            setMailinglist(e.currentTarget.checked)
          }}
        />

        <TextInput
          icon="pen-note"
          className="fi_txt req boldLabel"
          label="Valfrjálst: viltu láta tilvísun fylgja með?"
          name="reference"
          value={user.reference}
          onChange={onReferenceChange}
          onKeyUp={handleKeyUp}
          maxLength={30}
          boldLabel
        />

        <ArrowButton disabled={!!errorMessage} onClick={handleSubmit}>
          Halda áfram
        </ArrowButton>
      </Form>
    </div>
  )
}
